import { AttachFile } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import uploadImage from "api/upload/uploadImage";
import CircularLoader from "components/CircularLoader";
import CustomCard from "components/CustomCard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useSessionAuth from "hooks/useSessionAuth";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import updateEvent from "layouts/pages/events/actions/updateEvent";
import { cardStyles } from "layouts/pages/events/styles";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import ApplicantAttachmentsPreview from "./ApplicantAttachmentsPreview";
import Attachments from "./Attachments";

const useStyle = makeStyles({
  dropzone: {
    height: 170,
  },
});
const InterviewAttachments = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyle();
  const { currentEvent, setCurrentEvent, company } = useAppContextController();
  const { logoutAuth0User } = useSessionAuth();

  const allOptions = {
    fetchAll: true,
    filters: {
      _id: currentEvent?.applicants?.map((app) => app.applicantId || app.id)?.join(";"),
    },
  };

  const {
    data: applicants,
    isLoading: isLoadingApplicants,
    refetch: refetchApplicants,
  } = useQuery(
    ["interviewApplicants", allOptions],
    () => {
      return fetchApplicants(allOptions);
    },
    {
      enabled: !!currentEvent?.applicants.length,
    }
  );

  const isLoading = !!currentEvent?.applicants.length && isLoadingApplicants;

  const applicantAttachmentGroups = useMemo(() => {
    const result = [];
    if (applicants?.data?.length) {
      for (let index = 0; index < applicants?.data.length; index += 1) {
        const appl = applicants?.data[index];
        const validAttachments = (appl.attachments ?? []).filter(
          (att) => att.type === "Resume" || att.type === "AIScreening" || att.type === "Assessments"
        );

        if (validAttachments.length) {
          result.push({
            firstName: appl.firstName?.toUpperCase(),
            lastName: appl.lastName?.toUpperCase(),
            id: appl._id,
            attachments: validAttachments,
          });
        }
      }
    }

    return result;
  }, [applicants?.data]);

  const { mutate: uploadMutation } = useMutation(
    async (data) => {
      const form = new FormData();
      form.append("file", data.file);

      await uploadImage({
        uploadPath: `${currentEvent.companySlug}/events/${currentEvent?.eventUrl}`,
        form,
      });

      await updateEvent({ eventId: currentEvent._id, data: { eventImage: data.eventImage } });
    },
    {
      onError: (error) => enqueueSnackbar(`Error: ${error}`, { variant: "error" }),
      onSuccess: (_, data) => {
        setCurrentEvent({ ...currentEvent, ...data });
        enqueueSnackbar(`Image been updated!`, { variant: "success" });
      },
    }
  );

  const onDropImageHandler = (img) => {
    const file = img[0];
    uploadMutation({ eventImage: file.name, file });
  };

  return (
    <>
      <CustomCard
        icon={<AttachFile color="white" />}
        cardTitle="Interview Attachments"
        iconContainerSx={{ backgroundColor: "warning", mt: -3, ml: "1.5rem" }}
        cardSx={cardStyles.card}
        titleSx={cardStyles.title}
        isStacked
      >
        <Box sx={cardStyles.container}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={12}>
              <MDBox mt={2}>
                <Attachments
                  setCurrentInterview={setCurrentEvent}
                  currentInterview={currentEvent}
                />
                {isLoading && (
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{ flexDirection: "column", alignItems: "center", marginTop: "2rem" }}
                  >
                    <MDTypography component="label" variant="button" fontWeight="bold" color="info">
                      Loading applicant attachments
                    </MDTypography>
                    <MDBox
                      sx={{
                        position: "relative",
                        height: "50px",
                        width: "50px",
                      }}
                    >
                      <CircularLoader isLoading hasBackdrop={false} color="info" />
                    </MDBox>
                  </Grid>
                )}
                {applicantAttachmentGroups.map((appl) => (
                  <ApplicantAttachmentsPreview currentApplicant={appl} />
                ))}
              </MDBox>
            </Grid>
          </Grid>
        </Box>
      </CustomCard>
    </>
  );
};

export default InterviewAttachments;
